/**
 * @author Mr_FabiozZz[mr.fabiozzz@gmail.com]
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import sass from "./register.module.sass";
import useDebounce from "hooks/useDebounce";
import { useLocation, useNavigate } from "react-router-dom";
import { IBusinessProcessTemplate } from "pages/References/BusinessProcessRegistry/model";
import { snackWrapper } from "helpers";
import Table from "pages/References/BusinessProcessRegistry/Components/Table";
import {
	ConfirmArchiveTemplate,
	ConfirmModal,
	ICreateBusinessTemplate,
	Modal,
} from "pages/References/BusinessProcessRegistry/Components/modals";
import HeadTemplates from "pages/References/BusinessProcessRegistry/Components/HeadTemplates";
import DropDown from "pages/References/BusinessProcessRegistry/Components/DropDown";
import { organizationAPI } from "libs/apiClient/Organization";
import { BusinessProcessPatternInfoResponse, BusinessProcessPatternResponse } from "libs/apiClient/data-contracts";
import { userAPI } from "libs/apiClient/User";

const BusinessProcessRegistry = () => {
	const navigate = useNavigate();

	const [data, setData] = useState<BusinessProcessPatternResponse[]>([]);

	const [filter, setFilter] = useState<BusinessProcessPatternInfoResponse[] | null>(null);

	const [confirm, setConfirm] = useState(false);

	const [selectTemplate, setSelectTemplate] = useState(false);

	const toggleConfirmTemplate = () => {
		setSelectTemplate((prevState) => !prevState);
	};

	const location = useLocation();

	const archive = useMemo(() => {
		return /archive$/.test(location.pathname);
	}, [location.pathname]);

	const select = useMemo(() => {
		return /select/.test(location.pathname);
	}, [location.pathname]);

	const updateData = () => {
		organizationAPI.processesPatternsAllList().then((r) => setData(r.data));
	};

	const [modal, setModal] = useState<{ active: boolean; data: ICreateBusinessTemplate | null; loadTemplate?: boolean }>(
		{ active: false, data: null, loadTemplate: false },
	);

	const [search, setSearch] = useState("");

	const query = useDebounce(search, 400);

	const [target, setTarget] = useState<HTMLOrSVGElement | undefined>();
	const [currentPattern, setCurrentPattern] = useState("");

	const toggleTarget = (e: React.MouseEvent<HTMLOrSVGElement>) => {
		e.stopPropagation();
		setCurrentPattern(e.currentTarget.dataset.id as string);
		target ? setTarget(undefined) : setTarget(e.currentTarget);
	};
	const removeItem = () => {
		organizationAPI
			.processesPatternDisableCreate(+currentPattern)
			.then(() => {
				snackWrapper("success", "Запись отправлена в архив");
				setCurrentPattern("");
				setTarget(undefined);
				updateData();
				setConfirm(false);
			})
			.catch(() => {
				setCurrentPattern("");
				setTarget(undefined);
			});
	};

	const menuRef = useRef<HTMLDivElement | null>(null);

	const restoreTemplate = () => {
		const id: string = JSON.parse(currentPattern);
		// const url = `/organization/{id}/processes/pattern/${id}/enable`;
		organizationAPI.processesPatternEnableCreate(+id).then(() => {
			updateData();
			setTarget(undefined);
			setCurrentPattern("");
		});
	};

	const render = useMemo(() => {
		const renderData = filter ? filter : data;
		return (renderData as any).filter((_: any) => _.active === !archive);
	}, [filter, data, archive]);

	const rowClick = useCallback(
		(_: IBusinessProcessTemplate) => {
			if (select) {
				setCurrentPattern(`${_.patternId}`);
				toggleConfirmTemplate();
			} else {
				navigate(`${_.patternId}`);
			}
			//eslint-disable-next-line
		},
		//eslint-disable-next-line
		[select],
	);

	const editTemplate = useCallback(() => {
		const findObj = data.find((_) => _.patternId === Number(target?.dataset.id) || 0);
		if (findObj) {
			setModal({
				data: {
					name: findObj.name,
					comment: findObj.comment as any,
					id: findObj.patternId,
				},
				loadTemplate: false,
				active: true,
			});
		}
		//eslint-disable-next-line
	}, [target]);

	const confirmRedirect = () => {
		const id = location.state;
		if (id) {
			const pattern = data.find((_) => _.patternId === +currentPattern);
			if (pattern) {
				organizationAPI
					.processesPatternApplyPatternItemsCreate(
						id,
						pattern.bpItemsIdsList.map((_) => _.patternItemId),
					)
					.then(() => {
						navigate(-1);
					});
			}
		}
	};

	useEffect(() => {
		if (query as string) {
			organizationAPI
				.processesPatternsInfoCreate({
					active: !archive,
					orgId: 0,
					searchField: query,
				})
				.then((r) => setFilter(r.data));
		} else if (query === "") {
			setFilter(null);
		}
	}, [query, archive]);

	useEffect(() => {
		updateData();
	}, [location]);

	useEffect(() => {
		function handleClickOutside(event: any) {
			if (menuRef.current && target && !menuRef.current.contains(event.target)) {
				setTarget(undefined);
			}
		}

		document.addEventListener("mouseup", handleClickOutside);
		return () => {
			document.removeEventListener("mouseup", handleClickOutside);
		};
	}, [target]);
	const [disabled, setDisabled] = useState(false);
	const needRule = useCallback(() => {
		if (disabled) snackWrapper("error", "Не достаточно прав");
	}, [disabled]);
	useEffect(() => {
		userAPI
			.permissionsCheckCreate(0, ["dictionary.business_process_pattern.edit"])
			.then((r) => setDisabled(!r.data.length));
	}, []);
	return (
		<div className={sass.wrapper}>
			{confirm && (
				<ConfirmArchiveTemplate
					cancelWindow={() => setConfirm(false)}
					remove={() => {
						removeItem();
					}}
				/>
			)}
			{selectTemplate && (
				<ConfirmModal
					close={toggleConfirmTemplate}
					ok={() => {
						navigate(location.pathname + "/" + currentPattern, { state: location.state });
					}}
					redirect={confirmRedirect}
				/>
			)}
			<Modal
				rootFunc={updateData}
				trigger={modal}
				cancelWindow={() => setModal((prevState) => ({ ...prevState, active: false }))}
			/>
			<DropDown
				remove={() => (disabled ? needRule() : removeItem())}
				ref={menuRef}
				restoreTemplate={() => (disabled ? needRule() : restoreTemplate())}
				target={target}
				data={data}
				setCurrent={(str) => setCurrentPattern(str)}
				editTemplate={() => (disabled ? needRule() : editTemplate())}
				confirmArchive={() => (disabled ? needRule() : setConfirm(true))}
				current={currentPattern}
				archive={archive}
			/>
			<HeadTemplates
				disabled={disabled}
				archive={archive}
				select={select}
				search={search}
				changeSearch={(str) => setSearch(str)}
				createTemplate={() => setModal({ active: true, data: null })}
			/>

			<table>
				<thead>
					<tr>
						<th>Порядковый номер</th>
						<th>Название</th>
						<th>Организация</th>
						<th>Комментарий</th>
						<th />
					</tr>
				</thead>
			</table>
			<div data-comment-false={true} className={sass.scroll}>
				<Table
					render={render}
					confirmTemplate={toggleConfirmTemplate}
					toggleTarget={toggleTarget}
					setCurrent={setCurrentPattern}
					rowClick={rowClick}
					select={select}
				/>
			</div>
		</div>
	);
};

export default BusinessProcessRegistry;
